import React, { createContext, useContext, useState, useEffect } from 'react';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  // Initialize state from localStorage if available
  const [isFiltered, setIsFiltered] = useState(false);
  const [filterKey, setFilterKey] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [mailID, setMailID] = useState('');
  const [upName, setUpName] = useState('');
  const [loggedInMail, setLoggedInMail] = useState('');
  const [loggedInUPN, setLoggedInUPN] = useState('');
  const [loggedInName, setLoggedInName] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [idToken, setIdToken] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [reportType, setReportType] = useState('Direct Reports');

  const [filterValues, setFilterValues] = useState({
    managerName: '',
    userName: '',
    jobLevel: '',
    location: ''
  });

  // Initialize from localStorage on component mount
  useEffect(() => {
    try {
      // Load simple string values
      const storedMailID = localStorage.getItem('mailID');
      const storedUpName = localStorage.getItem('upName');
      const storedLoggedInMail = localStorage.getItem('loggedInMail');
      const storedLoggedInUPN = localStorage.getItem('loggedInUPN');
      const storedLoggedInName = localStorage.getItem('loggedInName');
      const storedAccessToken = localStorage.getItem('accessToken');
      const storedIdToken = localStorage.getItem('idToken');
      const storedIsAdmin = localStorage.getItem('isAdmin');
      const storedReportType = localStorage.getItem('reportType');

      // Load array values with JSON parsing
      const storedTableData = localStorage.getItem('tableData');
      const storedFilteredTableData = localStorage.getItem('filteredTableData');

      if (storedMailID) setMailID(storedMailID);
      if (storedUpName) setUpName(storedUpName);
      if (storedLoggedInMail) setLoggedInMail(storedLoggedInMail);
      if (storedLoggedInUPN) setLoggedInUPN(storedLoggedInUPN);
      if (storedLoggedInName) setLoggedInName(storedLoggedInName);
      if (storedAccessToken) setAccessToken(storedAccessToken);
      if (storedIdToken) setIdToken(storedIdToken);
      if (storedIsAdmin) setIsAdmin(storedIsAdmin === 'true');
      if (storedReportType) setReportType(storedReportType);

      if (storedTableData) {
        const parsedTableData = JSON.parse(storedTableData);
        setTableData(parsedTableData);
        // Initialize filtered data with table data if no separate filtered data exists
        if (!storedFilteredTableData) {
          setFilteredTableData(parsedTableData);
        }
      }

      if (storedFilteredTableData) {
        setFilteredTableData(JSON.parse(storedFilteredTableData));
      }

    } catch (error) {
      console.error('Error loading data from localStorage:', error);
    }
  }, []); // Empty dependency array - only run on mount

  // Save to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem('tableData', JSON.stringify(tableData));
    } catch (error) {
      console.error('Error saving tableData to localStorage:', error);
    }
  }, [tableData]);

  useEffect(() => {
    try {
      localStorage.setItem('filteredTableData', JSON.stringify(filteredTableData));
    } catch (error) {
      console.error('Error saving filteredTableData to localStorage:', error);
    }
  }, [filteredTableData]);

  useEffect(() => {
    localStorage.setItem('mailID', mailID);
  }, [mailID]);

  useEffect(() => {
    localStorage.setItem('upName', upName);
  }, [upName]);

  useEffect(() => {
    localStorage.setItem('loggedInMail', loggedInMail);
  }, [loggedInMail]);

  useEffect(() => {
    localStorage.setItem('loggedInUPN', loggedInUPN);
  }, [loggedInUPN]);

  useEffect(() => {
    localStorage.setItem('loggedInName', loggedInName);
  }, [loggedInName]);

  useEffect(() => {
    localStorage.setItem('accessToken', accessToken);
  }, [accessToken]);

  useEffect(() => {
    localStorage.setItem('idToken', idToken);
  }, [idToken]);

  useEffect(() => {
    localStorage.setItem('isAdmin', isAdmin.toString());
  }, [isAdmin]);

  useEffect(() => {
    localStorage.setItem('reportType', reportType);
  }, [reportType]);

  // Function to clear all persisted data (useful for logout)
  const clearPersistedData = () => {
    const keysToRemove = [
      'tableData', 'filteredTableData', 'mailID', 'upName', 
      'loggedInMail', 'loggedInUPN', 'loggedInName', 'accessToken', 
      'idToken', 'isAdmin', 'reportType'
    ];
    keysToRemove.forEach(key => localStorage.removeItem(key));
  };
  

  const toggleFilter = (key) => {
    if (key) {
      setIsFiltered(true);
      setFilterKey(key);
    } else {
      setIsFiltered(false);
      setFilterKey(null);
    }
  };

  const filterTableData = (key) => {
   // console.log(key);
    let filteredResults = [];
      if (reportType === 'Direct Reports') {
        filteredResults = tableData.filter(item => {
         return (
           item.managerEmail && item.managerEmail.toString().toLowerCase() === upName.toString().toLowerCase()
         );
       });
     }
     else if (reportType === 'Indirect Reports') {
        filteredResults = tableData.filter(item => {
         return (
           item.managerEmail && item.managerEmail.toString().toLowerCase() !== upName.toString().toLowerCase()
         );
       });
     }
     else {
       filteredResults = tableData;
     }
    if (key) {
      const filteredData = filteredResults.filter((item) => item.nineboxmapping === key);
      setFilteredTableData(filteredData);
    }
    else {
      setFilteredTableData(filteredResults);
    }
  };

  const filterTableDataBySummary = (filterType) => {
    let filteredResults = [];
      if (reportType === 'Direct Reports') {
        filteredResults = tableData.filter(item => {
         return (
           item.managerEmail && item.managerEmail.toString().toLowerCase() === upName.toString().toLowerCase()
         );
       });
     }
     else if (reportType === 'Indirect Reports') {
        filteredResults = tableData.filter(item => {
         return (
           item.managerEmail && item.managerEmail.toString().toLowerCase() !== upName.toString().toLowerCase()
         );
       });
     }
     else {
       filteredResults = tableData;
     }
    if (filterType === 'Top Talent') {
      const filteredData = filteredResults.filter((item) => item.nineboxmapping === 'Top Talent' || item.nineboxmapping === 'Expandable' || item.nineboxmapping === 'Promotable');
      setFilteredTableData(filteredData);
    }
    else if (filterType === 'Consistent') {
      const filteredData = filteredResults.filter((item) => item.nineboxmapping === 'Solid Performer' || item.nineboxmapping === 'Suitably Placed' || item.nineboxmapping === 'Valued Professional');
      setFilteredTableData(filteredData);
    }
    else if (filterType === 'Observe') {
      const filteredData = filteredResults.filter((item) => item.nineboxmapping === 'Needs Improvement' || item.nineboxmapping === 'Needs Performance Management' || item.nineboxmapping === 'Poor Fit');
      setFilteredTableData(filteredData);
    }
  };





  const AdvanceFilter = (filterType, value) => {
   // console.log(value);
   const updatedFilterValues = {
    ...filterValues,
    [filterType]: value
  };
  setFilterValues(updatedFilterValues);

    console.log(filterValues);
    const filteredData = tableData.filter((item) => {
      return (
        (updatedFilterValues.managerName === '' || item.managerName.toLowerCase().includes(updatedFilterValues.managerName.toLowerCase())) &&
            (updatedFilterValues.userName === '' || item.userName.toLowerCase().includes(updatedFilterValues.userName.toLowerCase())) &&
            (updatedFilterValues.jobLevel === '' || item.jobLevel.toLowerCase().includes(updatedFilterValues.jobLevel.toLowerCase())) &&
            (updatedFilterValues.location === '' || item.location.toLowerCase().includes(updatedFilterValues.location.toLowerCase()))
        );
    });

    setFilteredTableData(filteredData);
  };

  return (
    <AppContext.Provider value={{ reportType,setReportType, loggedInName, setLoggedInName, loggedInMail, loggedInUPN, setLoggedInMail, setLoggedInUPN,  isFiltered, filterKey, toggleFilter, tableData, setTableData, filteredTableData, setFilteredTableData, filterTableDataBySummary, filterTableData, AdvanceFilter, upName, setUpName, mailID, setMailID, accessToken, setAccessToken, isAdmin, setIsAdmin, idToken, setIdToken, clearPersistedData }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  return useContext(AppContext);
};
